/**
 * Types + fetchery dla pluginu `c_facture` rozszerzajacego shell `c_user_accounts`
 * (faktury zalogowanego usera - lista + pobranie PDF).
 *
 * Backend (IRESTAPI, routing po NAZWIE METODY - `IAPI_` strip):
 *   - GET /IRESTAPI/v1/C_FACTURE_CONTROLLER/userInvoices  (cały zbiór 12 mc + truncated)
 *
 * PDF pobierany jest bezposrednio z backendu przez zaszyfrowany link `pdf_href`
 * (`/IGETFILE/{blob}`) generowany po stronie serwera (`C_FACTURE_CONTROLLER::getUserInvoicePdfUrl`).
 * Wzorzec spojny z `M_FACTURE_MCONTROLLER::getPreviewOnlineUrl` - `id` faktury jest
 * w tamper-proof payloadzie, ownership sprawdzany po stronie backendu w handlerze
 * `userInvoicePdfFile` (parytet z FILE_ACCESS_LOGED + wlasny check `id_client == user`).
 *
 * Lista jest client-side gridem (TanStack Table, ten sam co lista userów) - backend
 * zwraca cały bounded zbiór, sort/filtr/wyszukiwarka/paginacja dzieją się w react-cms.
 */

/** Typy faktur (parytet z define.php: M_FACTURE_TYPE_*). */
export const FACTURE_TYPE_FVAT = 1;
export const FACTURE_TYPE_ORDER = 7;

export type UserInvoice = {
  id: number;
  /** Numer dokumentu. */
  nr: string;
  /** Data wystawienia (Y-m-d H:i:s). */
  add_date: string;
  comments: string;
  /** 7 = zamowienie, 1 = faktura VAT. */
  type: number;
  /** Ceny sformatowane przez backend (IFINANCE::getFormatedPrice). */
  total_price_netto: string;
  total_vat_tax: string;
  total_price_brutto: string;
  paid: string;
  /** Czy faktura w pelni oplacona (brutto - paid <= 0). */
  is_paid: boolean;
  /** Czy wyslano info o doplacie. */
  sent_paid_info: boolean;
  /**
   * Absolutny zaszyfrowany URL pobrania PDF (`/IGETFILE/{blob}` z backendu).
   * Generowany przez `C_FACTURE_CONTROLLER::getUserInvoicePdfUrl` - id faktury jest
   * w tamper-proof payloadzie, ownership sprawdzany server-side przy download'zie.
   * Browser nawiguje bezposrednio (bez proxy).
   */
  pdf_href: string;
};

export type UserInvoicesResponse = {
  ok: true;
  data: {
    invoices: UserInvoice[];
    /** `true` gdy lista zostala obcieta do limitu serwera (500). */
    truncated: boolean;
  };
};

/**
 * Pobiera listę faktur usera (12 miesięcy) z route handler proxy.
 *
 * @returns `{ invoices, truncated }`
 * @throws Error gdy fetch fail / response !ok (caller obsluguje przez try/catch).
 */
export async function fetchUserInvoices(): Promise<{
  invoices: UserInvoice[];
  truncated: boolean;
}> {
  const res = await fetch("/api/iiapi/c-facture/invoices", { cache: "no-store" });
  if (!res.ok) {
    throw new Error(`Fetch invoices failed: HTTP ${res.status}`);
  }
  const body = (await res.json()) as
    | UserInvoicesResponse
    | { ok: false; error?: string; message?: string };
  if (!("ok" in body) || body.ok !== true) {
    throw new Error(
      "message" in body ? body.message ?? "Fetch failed" : "Invalid response shape",
    );
  }
  return {
    invoices: body.data?.invoices ?? [],
    truncated: body.data?.truncated ?? false,
  };
}

